import { useEffect } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { CalendarRange, CalendarPlus, CalendarOff } from 'lucide-react'
import { useSchedulePending, refreshSchedulePending } from '../../lib/schedulePending.js'

/**
 * Second-level tabs inside ตารางเวร › ทันตแพทย์ — the doctor roster and the two
 * request queues that feed it (owner's call, 2026-08-20).
 *
 * Nested rather than sibling tabs on ตารางเวร: ขอเวร and การลา are doctor-only,
 * and as siblings they sat beside the EMPLOYEE roster, implying they applied to
 * it too. Under ทันตแพทย์ the grouping states what is true — this is the doctor
 * side of the roster, and these are the requests that change it.
 *
 * 🔑 No role gate here, unlike the ประกาศ tab in DentistLayout. This section is
 * RequireStaff (Manager/GM/HR/Admin) and the proxies are /api/hr/*, which is the
 * SAME set — so the gate and the proxy already agree and a tab can never load
 * for someone the API will refuse. That alignment is the reason these belong
 * here: under /manager/dentist they needed an extra gate to paper over a
 * mismatch, and HR lost leave approval as a side effect.
 *
 * Rendered as pills, not a second underline row — two identical tab rows stacked
 * read as one confused row. The pill level is visibly subordinate to the
 * underline level above it.
 */
const TABS = [
  { to: '', end: true, label: 'ตารางเวร', icon: CalendarRange, hint: 'ตารางออกตรวจรายเดือน' },
  { to: 'shift-request', label: 'ขอเวร', icon: CalendarPlus, hint: 'ประกาศหาหมอมาลงเวรที่ยังว่าง', badge: 'shift' },
  { to: 'leave', label: 'การลา', icon: CalendarOff, hint: 'คำขอลาของทันตแพทย์', badge: 'leave' },
]

/* Counts for ขอเวร and การลา. Both are work WAITING ON THE MANAGER — an open
 * request that already has doctors interested, and a leave request nobody has
 * decided. A shift request posted on Friday collects answers over the weekend,
 * and without a number on the tab nobody opens it until someone asks.
 *
 * 🔑 THESE PILLS AND THE SIDEBAR BADGE READ THE SAME STORE. This used to fetch
 * GET /api/hr/shift-request + GET /api/hr/doctor-leave — two full, enriched lists
 * (a `db.get Doctor` per leave row, two response sub-queries per shift row) pulled
 * down and thrown away to render two numbers. Once the same numbers had to appear
 * in the SIDEBAR, which is mounted on every page for every manager, that shape
 * became the missed-call badge of 2026-08-24 all over again — the one that served
 * the whole app Cloudflare Error 1102. Both now read src/lib/schedulePending.js,
 * backed by a count-in-the-database endpoint (Xano api 655). One store also means
 * the pill and the badge can never disagree, which is what makes either worth
 * looking at.
 *
 * NO autoLoad HERE — the sidebar owns the fetch and the poll. This only asks for a
 * refresh on mount, because the store may be up to five minutes old by the time
 * someone opens this page and the pills sit directly above the list they count. */

export default function DoctorScheduleLayout() {
  const counts = useSchedulePending()
  useEffect(() => { refreshSchedulePending() }, [])
  return (
    <div>
      <nav className="flex items-center gap-1 mb-5">
        {TABS.map(({ to, end, label, icon: Icon, hint, badge }) => (
          <NavLink
            key={to || 'index'}
            to={to ? `/manager/schedule/doctor/${to}` : '/manager/schedule/doctor'}
            end={end}
            title={hint}
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors ${
                isActive
                  ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-100 font-medium'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
              }`
            }
          >
            <Icon size={13} strokeWidth={1.75} />
            <span>{label}</span>
            {badge && counts[badge] > 0 && (
              <span className="ml-0.5 rounded-full bg-amber-100 text-amber-700 ring-1 ring-amber-200 px-1.5 text-[11px] font-semibold leading-[17px]">
                {counts[badge]}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </div>
  )
}
